import { ConstraintEntity } from "domain/entity/Criteria/ConstraintEntity";
import { ConstraintValueEntity } from "domain/entity/Criteria/ConstraintValueEntity";
import _ from "lodash";
import { comboBoxEvaluatorForStringOption, comboBoxEvaluatorOption } from "presentation/constant/CriteriaDropdownProps/CriteriaDropdownPropsOptions";
import { useCriteriaVM } from "presentation/hook/Criteria/useCriteriaVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { DropdownProps } from "presentation/model/DropdownProps";
import { useCriteriaTracked } from "presentation/store/Criteria/CriteriaProvider";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldType, IFieldValue } from "veronica-ui-component/dist/component/core";

const ConstraintItemPanel = ({
    panelKey,
    constraintItem,
    // criteriaState,
    onDeleteConstraintItem,
    onUpdateConstraintItem,
    itemBlur,
    onResetScoll,
}: {
    panelKey: string
    constraintItem: ConstraintEntity,
    // criteriaState: CriteriaModel,
    onDeleteConstraintItem: (removeItem: ConstraintEntity) => void,
    onUpdateConstraintItem: (updateItem: ConstraintEntity) => void,
    itemBlur: boolean
    onResetScoll: () => void,
}) => {
    const [criteriaState] = useCriteriaTracked();
    const [currentConstraintDef, setCurrentConstraintDef] = useState(constraintItem.constraintDef);
    const [constraintDefDisplayName, setConstraintDefDisplayName] = useState(constraintItem.constraintDef.constraintDefDisplayName);
    const [currentConstraintEvaluator, setCurrentConstraintEvaluator] = useState(constraintItem.constraintEvaluator);
    const [constraintValues, setConstraintValues] = useState(constraintItem.constraintValues);
    const [constraintShowValues, setConstraintShowValues] = useState<string[]>([]);
    const { allFormState, isRead, isSaveClicked, currentSelectItem } = criteriaState.componentState;
    const [comboBoxValueOption, setComboBoxValueOption] = useState<DropdownProps[]>();
    const [comboBoxDefOption, setComboBoxDefOption] = useState<DropdownProps[]>();
    // const [isMultipleValue, setIsMultipleValue] = useState(true);
    const criteriaVM = useCriteriaVM();
    const [panelZIndexCriteria, setPanelZIndexCriteria] = useState<{}>({ width: "20vw" });
    const [panelZIndexSymbol, setPanelZIndexSymbol] = useState<{}>({ width: "9vw" });
    const [panelZIndexValue, setPanelZIndexValue] = useState<{}>({ width: "19.5vw" });
    const [showDummyDivCriteria, setShowDummyDivCriteria] = useState<boolean>(false);
    const [showDummyDivSymbol, setShowDummyDivSymbol] = useState<boolean>(false);
    const [showDummyDivValue, setShowDummyDivValue] = useState<boolean>(false);
    const [isRefresh, setIsRefresh] = useState<boolean>(false);
    const [showBlankValues, setShowBlankValues] = useState<boolean>(true);
    const messageBarVM = useMessageBarVM();

    const defDisplayNameRef = useRef<any>();
    const evaluatorRef = useRef<any>();
    const valueRef = useRef<any>();

    const calculateScrollHeight = useCallback((ref: any) => {
        if (!ref?.current?.commonRef?.current?.inputLabel?.current) return;

        const dropdownRect = ref.current.commonRef.current.inputLabel.current.getBoundingClientRect();
        const buttonPanelRect = document.getElementById('CriteriaComponentPanel_button')?.getBoundingClientRect();

        const checkPanelExists = () => {
            const panelElement = document.querySelector(".p-autocomplete-panel");
            if (panelElement && buttonPanelRect) {
                const panelHeight = panelElement.getBoundingClientRect().height;
                const totalHeight = buttonPanelRect?.top - dropdownRect.bottom;

                if (totalHeight < 350 && totalHeight > 30 && panelHeight >= 50) {
                    (panelElement as HTMLElement).style.position = "absolute";
                    (panelElement as HTMLElement).style.bottom = `${dropdownRect.top + 45}px`;
                    (panelElement as HTMLElement).style.top = '';
                    (panelElement as HTMLElement).style.height = `${panelHeight + 25}px`;
                    (panelElement as HTMLElement).style.transform = `translateY(${-panelHeight}px)`;
                }
            } else {
                requestAnimationFrame(checkPanelExists);
            }
        };

        checkPanelExists();
    }, []);

    const fetchValueData = useCallback(async () => {
        if (constraintItem?.constraintDef?.constraintDefDisplayName) {
            setShowBlankValues(true);
            criteriaVM.getConsGroupDefValue(constraintItem.constraintDef.constraintDefDisplayName + ':' + constraintItem.constraintDef.constraintDefName).then((valueData) => {
                if (valueData && valueData.length > 0) {
                    setComboBoxValueOption(valueData);
                } else {
                    setComboBoxValueOption([]);
                }
                setShowBlankValues(false);
            }).finally(() => {
                setShowBlankValues(false);
            });
        }
    }, [constraintItem.constraintDef, criteriaVM]);

    useEffect(() => {
        fetchValueData();

        setConstraintValues(constraintItem.constraintValues);
        setCurrentConstraintDef(constraintItem.constraintDef);
        setConstraintDefDisplayName(constraintItem.constraintDef?.constraintDefDisplayName || '');
        setCurrentConstraintEvaluator(constraintItem.constraintEvaluator);
    }, [constraintItem.constraintDef, constraintItem.constraintEvaluator, constraintItem.constraintValues, fetchValueData]);

    useEffect(() => {
        if (showBlankValues) {
            setShowBlankValues(false);
        }
    }, [criteriaState.isForceRefresh, showBlankValues]);

    useEffect(() => {
        if (constraintValues && constraintValues.length) {
            setConstraintShowValues(constraintValues?.map(item => item.constraintValue));
        } else {
            setConstraintShowValues([]);
        }
    }, [constraintValues]);

    useEffect(() => {
        const cons = currentSelectItem.constraints;
        let comBoxDef: DropdownProps[];
        if (currentSelectItem.constraintGroupDefName) {
            comBoxDef = criteriaState.dynamicOptions.consDefDropdownOptions[currentSelectItem.constraintGroupDefName];
        } else {
            const newOptions = criteriaState.consGrpsTypeDropdownOptions[0];
            comBoxDef = criteriaState.dynamicOptions.consDefDropdownOptions[newOptions?.key ?? '']
        }
        comBoxDef = comBoxDef?.filter(item => !cons?.some(con => con.constraintDef.constraintDefDisplayName === item.value) ||
            item.value === currentConstraintDef.constraintDefDisplayName);
        setComboBoxDefOption(comBoxDef);
    }, [currentSelectItem, criteriaState.dynamicOptions.consDefDropdownOptions, criteriaState.consGrpsTypeDropdownOptions,
        currentConstraintDef.constraintDefDisplayName]);

    const onConstraintDefChange = useCallback(async (fieldKey: string, fieldValue: IFieldValue) => {
        const fieldValueStr = fieldValue?.toString();

        const selectedDef = criteriaState.groupConstraintDef[currentSelectItem.constraintGroupDefName]?.find((def) => def.constraintDefDisplayName === fieldValueStr);

        setConstraintDefDisplayName(fieldValueStr ?? '');
        if (selectedDef) {
            setCurrentConstraintDef(selectedDef);
            let consValueList: ConstraintValueEntity[] = [];
            setConstraintValues(consValueList);
            onUpdateConstraintItem({ ...constraintItem, constraintDef: selectedDef, constraintDefId: selectedDef.id, constraintValues: consValueList })
            //criteriaVM.getConsGroupDefValue(selectedDef.constraintDefDisplayName+':'+selectedDef.constraintDefName);
        }
    }, [constraintItem, criteriaState.groupConstraintDef, currentSelectItem.constraintGroupDefName, onUpdateConstraintItem]);

    const onConstraintEvaluatorChange = useCallback(async (fieldKey: string, fieldValue: IFieldValue) => {
        setShowBlankValues(true);
        const fieldValueStr = fieldValue?.toString() ?? null;
        let constraintValues: ConstraintValueEntity[] = constraintItem.constraintValues;
        if (currentConstraintEvaluator !== fieldValueStr) {
            setCurrentConstraintEvaluator(fieldValueStr);
            constraintValues = [];
        }
        setConstraintValues(constraintValues);
        onUpdateConstraintItem({ ...constraintItem, constraintEvaluator: fieldValueStr, constraintValues: constraintValues })
    }, [constraintItem, currentConstraintEvaluator, onUpdateConstraintItem]);

    const isMultipleValue = useCallback(() => {
        if (currentConstraintEvaluator === "IN" || currentConstraintEvaluator === "NOT_IN") {
            return true;
        } else {
            return false;
        }
    }, [currentConstraintEvaluator]);

    const onConstraintValuesChange = useCallback(async (fieldName: string, fieldValue: IFieldValue) => {
        let val = fieldValue;

        //check if constraint is string, and eveluator is equal and not_equal then just allow input one value
        if (!isMultipleValue() && !currentConstraintDef.constraintDefLovQuery?.trim()) {
            if (val?.toString().includes(",")) {
                messageBarVM.showError(`In group [${currentSelectItem.constraintGroupName}], only IN/NOT_IN item can input multiple values.`);
                return;
            }
        }

        let consValueList: ConstraintValueEntity[] = [];
        if (val) {
            if (_.isArray(val)) {
                val = _.uniq(val?.map((item: any) => item.value || item.key));

                consValueList = val.map(value => ({
                    id: null,
                    constraintId: constraintItem.id,
                    constraintValue: value
                }));
            } else {
                const stringValue = val?.toString().trim() ?? "";

                if (stringValue.length === 0) {
                    consValueList = [];
                } else if (stringValue.includes(',')) {
                    // Separate by comma, remove duplicates
                    const values = stringValue.split(',');


                    consValueList = values.map(value => ({
                        id: null,
                        constraintId: constraintItem.id,
                        constraintValue: value ?? ""
                    }));
                } else {
                    consValueList = [{
                        id: null,
                        constraintId: constraintItem.id,
                        constraintValue: stringValue ?? ""
                    }];
                }
            }
        } else {
            consValueList = []
        }

        setConstraintValues(consValueList);
        onUpdateConstraintItem({ ...constraintItem, constraintValues: consValueList })
    }, [isMultipleValue, currentConstraintDef.constraintDefLovQuery, onUpdateConstraintItem, constraintItem, messageBarVM,
        currentSelectItem.constraintGroupName]);

    const calculatePosition = useCallback((key: string) => {
        const pDiv = document.querySelector(`#${key}`);
        if (pDiv) {
            console.log(`Found element #constraintDefDisplayName-${key}`);

            let totalTop = 0;
            let totalLeft = 0;
            let currentElement = pDiv as HTMLElement;

            while (currentElement) {
                console.log(`Processing element: ${currentElement.id || currentElement.className}`, {
                    offsetTop: currentElement.offsetTop,
                    scrollTop: currentElement.scrollTop,
                    offsetLeft: currentElement.offsetLeft,
                    scrollLeft: currentElement.scrollLeft
                });

                totalTop += currentElement.offsetTop;
                totalLeft += currentElement.offsetLeft;

                let parent = currentElement.parentElement;
                while (parent) {
                    if (parent.scrollTop > 0 || parent.scrollLeft > 0) {
                        console.log(`Found scrollable parent: ${parent.id || parent.className}`);
                        totalTop -= parent.scrollTop;
                        totalLeft -= parent.scrollLeft;
                    }
                    parent = parent.parentElement;
                }

                currentElement = currentElement.offsetParent as HTMLElement;
            }

            console.log(`Final position for #constraintDefDisplayName-${key}:`, { left: totalLeft, top: totalTop });

            return {
                left: totalLeft,
                top: totalTop
            };
        } else {
            console.log(`Element #constraintDefDisplayName-${key} not found`);
        }
    }, []);

    const onFocus = useCallback((panelKey: string) => {
        const position = calculatePosition(panelKey);
        if (panelKey.includes('constraintDefDisplayName')) {
            setShowDummyDivCriteria(true);
            setPanelZIndexCriteria({ ...position, width: "20vw", zIndex: '99', overflow: 'visible', position: 'fixed' });
            setIsRefresh(!isRefresh);
        }
        if (panelKey.includes('constraintEvaluator')) {
            setShowDummyDivSymbol(true);
            setPanelZIndexSymbol({ ...position, width: "9vw", zIndex: '99', overflow: 'visible', position: 'fixed' });
        }
        if (panelKey.includes('constraintValue')) {
            setShowDummyDivValue(true);
            setPanelZIndexValue({ ...position, width: "19.5vw", zIndex: '99', overflow: 'visible', position: 'fixed' });
        }
        if (onResetScoll) {
            onResetScoll();
        }
    }, [calculatePosition, onResetScoll, isRefresh])

    const onBlur = useCallback(() => {
        setPanelZIndexCriteria({ width: "20vw" });
        setPanelZIndexSymbol({ width: "9vw" });
        setPanelZIndexValue({ width: "19.5vw" });
        setShowDummyDivCriteria(false);
        setShowDummyDivSymbol(false);
        setShowDummyDivValue(false);
        setIsRefresh(!isRefresh);
    }, [isRefresh]);

    useEffect(() => {
        if (itemBlur) {
            onBlur();
        }
    }, [itemBlur, onBlur])

    const memoCriteria = useMemo(() => {
        return <NbisCommonField
            errorMessages={allFormState}
            isReadOnly={isRead}
            isShowOptional={false}
            readOnlyValue={/*currentConstraintDef?.*/constraintDefDisplayName ?? ''}
            fieldValue={/*currentConstraintDef?.*/constraintDefDisplayName ?? ''}
            isSaveClicked={isSaveClicked}
            fieldType={FieldType.DROPDOWN}
            fieldKey={'constraintDefDisplayName'}
            options={comboBoxDefOption}
            maxLength={60}
            isShowMissingError={true}
            requiredFieldList={['constraintDefDisplayName']}
            onFieldChange={onConstraintDefChange}
            onFocus={() => onFocus(`constraintDefDisplayName-${panelKey}`)}
            onBlur={onBlur}
            isCustomBlur={true}
            ref={defDisplayNameRef}
        //onDropdownClick={() => calculateScrollHeight(defDisplayNameRef)}

        />
    }, [allFormState, isRead, isSaveClicked, constraintDefDisplayName, comboBoxDefOption, onConstraintDefChange, onFocus, panelKey, onBlur]);

    const memoSymbol = useMemo(() => {
        return <NbisCommonField
            errorMessages={allFormState}
            isReadOnly={isRead}
            isShowOptional={false}
            readOnlyValue={currentConstraintEvaluator ?? ''}
            fieldValue={currentConstraintEvaluator ?? ''}
            isSaveClicked={isSaveClicked}
            fieldType={FieldType.DROPDOWN}
            fieldKey={'constraintEvaluator'}
            options={currentConstraintDef?.constraintDefLovRefColumnTypes === 'String' ? comboBoxEvaluatorForStringOption : comboBoxEvaluatorOption}
            maxLength={60}
            isShowMissingError={true}
            requiredFieldList={['constraintEvaluator']}
            onFieldChange={onConstraintEvaluatorChange}
            onFocus={() => onFocus(`constraintEvaluator-${panelKey}`)}
            onBlur={onBlur}
            isCustomBlur={true}
            ref={evaluatorRef}
        //onDropdownClick={() => calculateScrollHeight(evaluatorRef)}
        />
    }, [allFormState, isRead, isSaveClicked, currentConstraintEvaluator, currentConstraintDef, onConstraintEvaluatorChange, onFocus, panelKey, onBlur]);

    const memoValue = useMemo(() => {
        return currentConstraintDef?.constraintDefLovQuery?.trim() ?
            (showBlankValues ?
                <NbisCommonField
                    errorMessages={allFormState}
                    isReadOnly={isRead}
                    isShowOptional={false}
                    isSaveClicked={isSaveClicked}
                    fieldType={currentConstraintDef?.constraintDefLovQuery?.trim() ? FieldType.DROPDOWN : FieldType.TEXT}
                    fieldKey={'constraintValue'}
                    maxLength={100}
                    isShowMissingError={true}
                    isMultipleDropDown={false}
                    requiredFieldList={['constraintValue']}
                    onFieldChange={onConstraintValuesChange}
                    ref={valueRef}
                /> :
                <NbisCommonField
                    errorMessages={allFormState}
                    isReadOnly={isRead}
                    isShowOptional={false}
                    readOnlyValue={constraintShowValues && constraintShowValues.length ? constraintShowValues.join(',') : ''}
                    fieldValue={isMultipleValue() ?
                        ((constraintShowValues && constraintShowValues.length) ? constraintShowValues : []) :
                        ((constraintShowValues && constraintShowValues.length) ? constraintShowValues?.[0] : '')}
                    isSaveClicked={isSaveClicked}
                    allValues={constraintShowValues && constraintShowValues.length ? constraintShowValues.join(',') : ''}
                    fieldType={FieldType.DROPDOWN}
                    fieldKey={'constraintValue'}
                    options={comboBoxValueOption}
                    maxLength={100}
                    isShowMissingError={true}
                    isMultipleDropDown={isMultipleValue()}
                    requiredFieldList={['constraintValue']}
                    onFieldChange={onConstraintValuesChange}
                    // onFocus={() => onFocus(`constraintValue-${panelKey}`)}
                    // onBlur={onBlur}
                    ref={valueRef}
                //onDropdownClick={() => calculateScrollHeight(valueRef)}
                />) :
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isRead}
                isShowOptional={false}
                readOnlyValue={constraintShowValues.join(',')}
                fieldValue={constraintShowValues.join(',')}
                isSaveClicked={isSaveClicked}
                allValues={constraintShowValues.join(',')}
                fieldType={currentConstraintDef?.constraintDefLovQuery?.trim() ? FieldType.DROPDOWN : FieldType.TEXT}
                fieldKey={'constraintValue'}
                options={currentConstraintDef?.constraintDefLovQuery?.trim() ? comboBoxValueOption : undefined}
                maxLength={100}
                isShowMissingError={true}
                isMultipleDropDown={isMultipleValue()}
                requiredFieldList={['constraintValue']}
                onFieldChange={onConstraintValuesChange}
                // onFocus={() => onFocus(`constraintValue-${panelKey}`)}
                // onBlur={onBlur}
                ref={valueRef}
            //onDropdownClick={() => calculateScrollHeight(valueRef)}
            />
    }, [allFormState, comboBoxValueOption, constraintShowValues, currentConstraintDef?.constraintDefLovQuery,
        isMultipleValue, isRead, isSaveClicked, onConstraintValuesChange, showBlankValues]);

    const memoDeleteIcon = useMemo(() => {
        return <NbisIconButton fileName="Icon-trash" toolTipText="Delete" disabled={isRead} size="medium" onClick={() => onDeleteConstraintItem(constraintItem)} />
    }, [constraintItem, isRead, onDeleteConstraintItem]);

    return (
        <div className='im-flex-row' id={`constraintItem-${panelKey}`}>
            <div id={`constraintDefDisplayName-${panelKey}`} className={`im-flex-row-item`} style={{ ...panelZIndexCriteria }}>
                {memoCriteria}
            </div>
            {showDummyDivCriteria && <div className={"im-flex-row-item"} style={{ width: "20vw" }}></div>}
            <div id={`constraintEvaluator-${panelKey}`} className={`im-flex-row-item`} style={{ ...panelZIndexSymbol }}>
                {memoSymbol}
            </div>
            {showDummyDivSymbol && <div className={"im-flex-row-item"} style={{ width: "9vw" }}></div>}
            <div id={`constraintValue-${panelKey}`} className={`im-flex-row-item`} style={{ ...panelZIndexValue }}>
                {memoValue}
            </div>
            {showDummyDivValue && <div className={"im-flex-row-item"} style={{ width: "19.5vw" }}></div>}

            <div className='im-flex-row-item' style={{ width: "30px" }}>
                {memoDeleteIcon}
            </div>
            {criteriaState.isForceRefresh && <></>}
        </div>
    );
};

export default memo(ConstraintItemPanel);